// Web-exclusive Home screen - Facility Dashboard
// Matching reference design from facilities-pages.html

import React, { useState, useMemo, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator, Modal, Pressable } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import {
  Building2, Briefcase, Globe, Clock, FileEdit, ClipboardList, Users, CheckCircle,
  Hourglass, BarChart3, User, HelpCircle, MessageSquare, ChevronDown, Check, ChevronRight,
  ChevronLeft, Calendar, X, QrCode,
} from 'lucide-react';
import QRCode from 'react-native-qrcode-svg';
import WebLayout from '../../components/web/WebLayout';
import JobDescriptionModal from '../../components/web/JobDescriptionModal.web';
import UpcomingJobsModal from '../../components/web/UpcomingJobsModal.web';
import { webColors, webSpacing, webBorderRadius, webShadows } from '../../constants/webStyles';
import { getOverview } from '../../services/profile';
import { setCountry, setCurrencyPreset } from '../../reducers/auth/user';
import { getApplications, getJobQR, putJobQR, approveJob, rejectJob } from '../../services/job';
import { JOB_TYPES, convertIndexToApiId } from '../../constants/jobTypes';
import { isFullTimeJob } from '../../constants/lov/professions';
import statusFilter from '../../utils/statusFilter';
import { getWorldCountryData } from '../../constants/worldCountries';
import { formatPrice, formatPriceRange } from '../../utils/currency';
import { useIsMobile } from '../../hooks/useIsMobile';

const HomeFacilityScreen = () => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const queryClient = useQueryClient();

  // State
  const [activeCategory, setActiveCategory] = useState(0); // 0 = Locum, 1 = Full-Time, etc.
  const [activeStatusTab, setActiveStatusTab] = useState('all');
  const [selectedProfessionFilter, setSelectedProfessionFilter] = useState(0);
  const [sortBy, setSortBy] = useState('Latest');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showJobModal, setShowJobModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showUpcomingModal, setShowUpcomingModal] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [showCheckInConfirm, setShowCheckInConfirm] = useState(false);
  const [checkInJob, setCheckInJob] = useState(null);
  const [showCheckInQR, setShowCheckInQR] = useState(false);
  const isMobile = useIsMobile();

  // Categories with icons
  const categories = [
    { id: 0, label: 'Locum', Icon: Building2 },
    { id: 1, label: 'Full-Time', Icon: Briefcase },
    { id: 2, label: 'International', Icon: Globe },
    { id: 3, label: 'Temporary', Icon: Clock },
    { id: 4, label: 'Contract', Icon: FileEdit },
  ];

  // Status tabs for facilities
  const statusTabs = [
    { id: 'all', label: 'All' },
    { id: 'approved', label: 'Approved' },
    { id: 'pending', label: 'Pending' },
    { id: 'unfilled', label: 'Unfilled' },
    { id: 'cancelled', label: 'Cancelled' },
    { id: 'draft', label: 'Draft' },
    { id: 'history', label: 'History' },
  ];

  // Build query string for job type filter
  const jobTypeQuery = useMemo(() => {
    const apiJobType = convertIndexToApiId(activeCategory);
    if (apiJobType !== undefined) {
      const draftJobType = -(apiJobType + 10);
      return `job_type=${apiJobType},${draftJobType}`;
    }
    return '';
  }, [activeCategory]);

  // Fetch dashboard data
  const { data: overviewData, isLoading: overviewLoading } = useQuery({
    queryKey: ['overview'],
    queryFn: getOverview,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });
  const overview = overviewData?.data?.data;

  useEffect(() => {
    if (overview?.country) {
      dispatch(setCurrencyPreset(overview.country));
      dispatch(setCountry(overview.country));
    }
  }, [overview]);

  const { data: applicationsData, isLoading: applicationsLoading, refetch: applicationsRefetch } = useQuery({
    queryKey: ['applications', { query: jobTypeQuery }],
    queryFn: getApplications,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });
  const allJobs = applicationsData?.data?.data ? Object.values(applicationsData.data.data) : [];

  // Callback to refresh data after modal actions
  const handleRefresh = () => {
    applicationsRefetch();
  };

  // Direct check-in mutation: fetches QR then submits scan
  const directCheckIn = useMutation({
    mutationFn: async (jobUuid) => {
      const qrResponse = await getJobQR(jobUuid);
      const qrToken = qrResponse?.data?.data?.qr;
      if (!qrToken) throw new Error('Unable to generate check-in code');
      return putJobQR({ id: qrToken });
    },
    onSuccess: () => {
      setShowCheckInConfirm(false);
      setCheckInJob(null);
      setShowCheckInQR(false);
      applicationsRefetch();
    },
    onError: (error) => {
      alert(error?.response?.data?.message || error?.message || 'Check-in failed. Please try again.');
    },
  });

  // QR code for the "Show QR" view inside the Check In Doctor modal (doctor scans to check in)
  const { data: checkInQrData, isLoading: checkInQrLoading } = useQuery({
    queryKey: ['jobQR', checkInJob?.uuid],
    queryFn: () => getJobQR(checkInJob?.uuid),
    enabled: showCheckInQR && !!checkInJob?.uuid,
  });
  const checkInQrToken = checkInQrData?.data?.data?.qr;

  const closeCheckInModal = () => {
    setShowCheckInConfirm(false);
    setCheckInJob(null);
    setShowCheckInQR(false);
  };

  const handleDirectCheckIn = (job) => {
    setCheckInJob(job);
    setShowCheckInQR(false);
    setShowCheckInConfirm(true);
  };

  const confirmCheckIn = () => {
    if (checkInJob?.uuid) {
      directCheckIn.mutate(checkInJob.uuid);
    }
  };

  // Approve applicant mutation (facility approves a doctor's application)
  const approveMutation = useMutation({
    mutationFn: approveJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      applicationsRefetch();
    },
    onError: (error) => {
      alert('Failed to approve applicant: ' + (error.response?.data?.message || 'Something went wrong'));
    },
  });

  // Reject applicant mutation
  const rejectMutation = useMutation({
    mutationFn: rejectJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      applicationsRefetch();
    },
    onError: (error) => {
      alert('Failed to reject applicant: ' + (error.response?.data?.message || 'Something went wrong'));
    },
  });

  // Build a readable doctor name from an applicant object
  const getApplicantName = (applicant) => {
    const doctor = applicant?.doctor || applicant?.user || {};
    const name = [doctor.first_name, doctor.last_name].filter(Boolean).join(' ').trim();
    return name || doctor.name || applicant?.name || 'this applicant';
  };

  // Handle approve with confirmation
  const handleApproveApplicant = (job, applicant) => {
    if (!applicant?.uuid) {
      alert('Unable to approve: Application data is missing');
      return;
    }

    const confirmed = window.confirm(
      `Approve ${getApplicantName(applicant)} for this job?\n\n` +
      `${job.title}\n` +
      `${moment(job.start_timestamp).format('MMM D, YYYY • h:mm A')}`
    );

    if (confirmed) {
      approveMutation.mutate({ uuid: applicant.uuid });
    }
  };

  // Handle reject with confirmation
  const handleRejectApplicant = (job, applicant) => {
    if (!applicant?.uuid) {
      alert('Unable to reject: Application data is missing');
      return;
    }

    const confirmed = window.confirm(
      `Reject ${getApplicantName(applicant)}'s application?\n\n` +
      `${job.title}\n\n` +
      `This action cannot be undone.`
    );

    if (confirmed) {
      rejectMutation.mutate({ uuid: applicant.uuid });
    }
  };

  // Get unique profession types for filter tabs
  const professionTabs = useMemo(() => {
    const titles = [...new Set(allJobs
      .filter(job => parseInt(job.type) > -10)
      .map(job => job.title))]
      .filter(Boolean)
      .sort();
    return [{ name: 'All', id: -1 }, ...titles.map((title, index) => ({ name: title, id: index }))];
  }, [allJobs]);

  // Filter jobs based on status for facilities
  const filteredJobs = useMemo(() => {
    return allJobs.filter(job => {
      const jobStatus = job.job_application_status_code;
      const isDraft = parseInt(job.type) <= -10;
      const hasApplications = job.job_applications && job.job_applications.length > 0;
      const isExpired = moment(job.end_timestamp).isBefore(moment());

      // Hide jobs that ended more than 24 hours ago (except drafts)
      if (!isDraft && job.end_timestamp) {
        const endTime = moment(job.end_timestamp);
        const twentyFourHoursAgo = moment().subtract(24, 'hours');
        if (endTime.isBefore(twentyFourHoursAgo) && (jobStatus === 4 || jobStatus === -2 || isExpired)) {
          return false;
        }
      }

      // Filter by status
      let passesStatus;
      switch (activeStatusTab) {
        case 'all':
          passesStatus = true;
          break;
        case 'draft':
          passesStatus = isDraft;
          break;
        case 'unfilled':
          passesStatus = !isDraft && !hasApplications && !isExpired;
          break;
        case 'pending':
          passesStatus = !isDraft && statusFilter('pending', job);
          break;
        case 'approved':
          passesStatus = !isDraft && jobStatus === 3;
          break;
        case 'cancelled':
          passesStatus = !isDraft && jobStatus === -2;
          break;
        case 'completed':
          passesStatus = !isDraft && jobStatus === 4;
          break;
        case 'expired':
          passesStatus = !isDraft && isExpired && jobStatus !== 4;
          break;
        default:
          passesStatus = true;
      }
      if (!passesStatus) return false;

      // Profession filter (index 0 = "All")
      if (selectedProfessionFilter !== 0) {
        const selectedTab = professionTabs[selectedProfessionFilter];
        if (!selectedTab || job.title !== selectedTab.name) return false;
      }

      return true;
    });
  }, [allJobs, activeStatusTab, selectedProfessionFilter, professionTabs]);

  // Sort jobs
  const sortedJobs = useMemo(() => {
    const sorted = [...filteredJobs];
    if (sortBy === 'Latest') {
      sorted.sort((a, b) => moment(b.start_timestamp) - moment(a.start_timestamp));
    } else if (sortBy === 'Oldest') {
      sorted.sort((a, b) => moment(a.start_timestamp) - moment(b.start_timestamp));
    } else if (sortBy === 'Urgent') {
      sorted.sort((a, b) => (a.is_emergency === b.is_emergency ? 0 : a.is_emergency ? -1 : 1));
    }
    return sorted;
  }, [filteredJobs, sortBy]);

  // Get all upcoming approved jobs (jobs in the future with approved applicants)
  const upcomingJobs = useMemo(() => {
    return allJobs
      .filter(job => job.job_application_status_code === 3)
      .filter(job => moment(job.start_timestamp).diff(moment()) > 0)
      .sort((a, b) => new Date(a.start_timestamp) - new Date(b.start_timestamp));
  }, [allJobs]);

  // Get upcoming job (next approved job in the future)
  const upcomingJob = upcomingJobs[0];

  // Reset carousel index when upcomingJobs changes
  useEffect(() => {
    if (carouselIndex >= upcomingJobs.length) {
      setCarouselIndex(Math.max(0, upcomingJobs.length - 1));
    }
  }, [upcomingJobs.length, carouselIndex]);

  // Count jobs by category using backend job_stats from overview
  const categoryCounts = useMemo(() => {
    const jobStats = overview?.job_stats || {};
    const counts = {};
    categories.forEach(cat => {
      const apiType = convertIndexToApiId(cat.id);
      // job_stats uses string keys: "locum" for type 0, numeric strings for others
      const count = apiType === 0
        ? (jobStats['locum'] || jobStats['0'] || 0)
        : (jobStats[apiType] || jobStats[`${apiType}`] || 0);
      counts[cat.id] = count;
    });
    return counts;
  }, [overview]);

  // Stats data
  const stats = useMemo(() => {
    const activeJobs = allJobs.filter(j =>
      !moment(j.end_timestamp).isBefore(moment()) &&
      parseInt(j.type) > -10
    ).length;
    const totalApplications = allJobs.reduce((acc, j) => acc + (j.job_applications?.length || 0), 0);
    const filledPositions = allJobs.filter(j => j.job_applications?.length > 0 && j.job_application_status_code === 3).length;
    const pendingReview = allJobs.filter(j => statusFilter('pending', j)).length;

    return [
      { Icon: ClipboardList, label: 'Active\nJobs', value: activeJobs, bg: '#fce7f3' },
      { Icon: Users, label: 'Total\nApplications', value: totalApplications, bg: '#dbeafe' },
      { Icon: CheckCircle, label: 'Filled\nPositions', value: filledPositions, bg: '#dcfce7' },
      { Icon: Hourglass, label: 'Pending\nReview', value: pendingReview, bg: '#fef3c7' },
    ];
  }, [allJobs]);

  // Chart data
  const chartData = useMemo(() => {
    const isNonLocum = activeCategory !== 0;

    if (isNonLocum) {
      const applied = allJobs.filter(j => {
        const status = j.job_application_status_code;
        const isExpired = moment(j.end_timestamp).isBefore(moment()) && status !== 4;
        return [null, 0, 1].includes(status) && !isExpired;
      }).length;
      const expired = allJobs.filter(j => {
        return moment(j.end_timestamp).isBefore(moment()) && j.job_application_status_code !== 4;
      }).length;
      const maxVal = Math.max(applied, expired, 1);
      return [
        { label: 'Posted', value: applied, height: (applied / maxVal) * 100, color: '#59C2FF' },
        { label: 'Expired', value: expired, height: (expired / maxVal) * 100, color: '#808080' },
      ];
    }

    const cancelled = allJobs.filter(j => j.job_application_status_code === -2).length;
    const pending = allJobs.filter(j => statusFilter('pending', j)).length;
    const approved = allJobs.filter(j => j.job_application_status_code === 3).length;
    const completed = allJobs.filter(j => j.job_application_status_code === 4).length;
    const maxVal = Math.max(cancelled, pending, approved, completed, 1);
    return [
      { label: 'Cancelled', value: cancelled, height: (cancelled / maxVal) * 100, color: '#fee2e2' },
      { label: 'Pending', value: pending, height: (pending / maxVal) * 100, color: '#fef3c7' },
      { label: 'Approved', value: approved, height: (approved / maxVal) * 100, color: '#22d3ee' },
      { label: 'Completed', value: completed, height: (completed / maxVal) * 100, color: '#dcfce7' },
    ];
  }, [allJobs, activeCategory]);

  // Quick actions
  const quickActions = [
    { Icon: User, label: 'My Profile', bg: '#fce7f3', action: () => navigation.navigate('ProfileFacility') },
    { Icon: HelpCircle, label: 'Help & Support', bg: '#dcfce7', action: () => navigation.navigate('HelpSupportScreen') },
    { Icon: MessageSquare, label: 'Give us Feedback', bg: '#fef3c7', action: () => navigation.navigate('FeedbackScreen') },
  ];

  // Recent activity
  const recentActivity = useMemo(() => {
    const activities = [];
    allJobs.slice(0, 4).forEach(job => {
      const status = job.job_application_status_code;
      const hasApplicants = job.job_applications?.length > 0;
      let type = 'posted';
      let text = `Job posted: ${job.title}`;

      if (status === 3) {
        type = 'approved';
        text = `Position filled for ${job.title}`;
      } else if (hasApplicants) {
        type = 'applied';
        text = `${job.job_applications.length} new application(s) for ${job.title}`;
      }

      activities.push({
        type,
        text,
        title: job.title,
        time: formatTimeAgo(job.created_at || job.start_timestamp),
      });
    });
    return activities;
  }, [allJobs]);

  const isLoading = overviewLoading || applicationsLoading;

  return (
    <WebLayout>
      <ScrollView style={styles.scrollContainer} showsVerticalScrollIndicator={false}>
        {/* Header Row */}
        <View style={styles.headerRow}>
          <Text style={[styles.pageTitle, isMobile && styles.pageTitleMobile]}>Dashboard</Text>
          <View style={styles.headerActions}>
            <TouchableOpacity
              style={styles.btnPostJob}
              onPress={() => navigation.navigate('PostJob')}
            >
              <Text style={styles.btnPostJobIcon}>+</Text>
              <Text style={styles.btnPostJobText}>Post a Job</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Category Tabs */}
        <View style={[styles.categoryTabs, isMobile && styles.categoryTabsMobile]}>
          {categories.map((cat) => (
            <TouchableOpacity
              key={cat.id}
              style={[
                styles.categoryTab,
                activeCategory === cat.id && styles.categoryTabActive,
                isMobile && styles.categoryTabMobile,
              ]}
              onPress={() => setActiveCategory(cat.id)}
            >
              <Text style={[
                styles.categoryTabText,
                activeCategory === cat.id && styles.categoryTabTextActive,
              ]}>
                {cat.label}
              </Text>
              {categoryCounts[cat.id] > 0 && (
                <View style={[
                  styles.categoryBadge,
                  activeCategory === cat.id && styles.categoryBadgeActive,
                ]}>
                  <Text style={styles.categoryBadgeText}>{categoryCounts[cat.id]}</Text>
                </View>
              )}
            </TouchableOpacity>
          ))}
        </View>



        {/* Top Row Grid - Post CTA + Upcoming */}
        {activeCategory === 0 ? (
          <View style={[styles.topRowGrid, isMobile && styles.topRowGridMobile]}>
            {/* Post Job CTA */}
            <TouchableOpacity
              style={[styles.postJobCta, isMobile && styles.postJobCtaMobile]}
              onPress={() => navigation.navigate('PostJob')}
            >
              <View style={[styles.ctaContent, isMobile && styles.ctaContentMobile]}>
                <View style={styles.ctaIcon}>
                  <Briefcase size={20} color="#ffffff" />
                </View>
                <View style={[styles.ctaText, isMobile && styles.ctaTextMobile]}>
                  <Text style={styles.ctaTitle}>Post a Job</Text>
                  <Text style={styles.ctaSubtitle}>Find quality healthcare professionals</Text>
                </View>
              </View>
              <TouchableOpacity style={styles.ctaBtn} onPress={() => navigation.navigate('PostJob')}>
                <Text style={styles.ctaBtnText}>Post Job →</Text>
              </TouchableOpacity>
            </TouchableOpacity>

            {/* Upcoming Jobs Carousel */}
            {upcomingJobs.length > 0 && upcomingJobs[carouselIndex] ? (() => {
              const currentJob = upcomingJobs[carouselIndex];
              return (
                <View style={styles.upcomingCarousel}>
                  {/* Carousel Header */}
                  <View style={styles.carouselHeader}>
                    <Text style={styles.carouselHeaderText}>UPCOMING LOCUM JOBS</Text>
                    <View style={styles.carouselCountBadge}>
                      <Text style={styles.carouselCountBadgeText}>{upcomingJobs.length}</Text>
                    </View>
                  </View>

                  {/* Carousel Body */}
                  <TouchableOpacity
                    style={styles.carouselBody}
                    onPress={() => setShowUpcomingModal(true)}
                    activeOpacity={0.9}
                  >
                    <View style={styles.carouselContent}>
                      {/* Left Arrow */}
                      <TouchableOpacity
                        style={[styles.carouselNavBtn, carouselIndex === 0 && styles.carouselNavBtnDisabled]}
                        onPress={(e) => { e.stopPropagation(); setCarouselIndex(Math.max(0, carouselIndex - 1)); }}
                        disabled={carouselIndex === 0}
                      >
                        <ChevronLeft size={20} color={carouselIndex === 0 ? '#d1d5db' : '#6b7280'} />
                      </TouchableOpacity>

                      {/* Job Content */}
                      <View style={styles.carouselJobContent}>
                        <Text style={styles.carouselJobTitle} numberOfLines={1}>
                          {currentJob.title}
                        </Text>
                        <Text style={styles.carouselJobClinic}>
                          {currentJob.clinic?.name || currentJob.clinic_name || 'Healthcare Facility'}
                        </Text>

                        <View style={styles.carouselJobMeta}>
                          <View style={styles.carouselMetaItem}>
                            <Calendar size={14} color="#EC4899" />
                            <Text style={styles.carouselMetaText}>
                              {moment(currentJob.start_timestamp).format('D MMM')}
                            </Text>
                          </View>
                          <View style={styles.carouselMetaItem}>
                            <Clock size={14} color="#EC4899" />
                            <Text style={styles.carouselMetaText}>
                              {moment(currentJob.start_timestamp).format('h:mm A')} - {moment(currentJob.end_timestamp).format('h:mm A')}
                            </Text>
                          </View>
                        </View>

                        {currentJob.incentives && (
                          <View style={styles.carouselIncentiveBadge}>
                            <Text style={styles.carouselIncentiveBadgeText}>With other incentives</Text>
                          </View>
                        )}
                      </View>

                      {/* Right Arrow */}
                      <TouchableOpacity
                        style={[styles.carouselNavBtn, carouselIndex === upcomingJobs.length - 1 && styles.carouselNavBtnDisabled]}
                        onPress={(e) => { e.stopPropagation(); setCarouselIndex(Math.min(upcomingJobs.length - 1, carouselIndex + 1)); }}
                        disabled={carouselIndex === upcomingJobs.length - 1}
                      >
                        <ChevronRight size={20} color={carouselIndex === upcomingJobs.length - 1 ? '#d1d5db' : '#6b7280'} />
                      </TouchableOpacity>
                    </View>

                    {/* Dot Indicators */}
                    <View style={styles.carouselDots}>
                      {upcomingJobs.map((_, index) => (
                        <TouchableOpacity
                          key={index}
                          style={[styles.carouselDot, index === carouselIndex && styles.carouselDotActive]}
                          onPress={(e) => { e.stopPropagation(); setCarouselIndex(index); }}
                        />
                      ))}
                    </View>

                    {/* Check-in Button */}
                    {currentJob.is_scanned ? (
                      <View style={styles.carouselCheckedInBadge}>
                        <CheckCircle size={16} color="#22c55e" />
                        <Text style={styles.carouselCheckedInText}>Checked In</Text>
                      </View>
                    ) : (
                      <TouchableOpacity
                        style={styles.carouselCheckInBtn}
                        onPress={(e) => { e.stopPropagation(); handleDirectCheckIn(currentJob); }}
                      >
                        <Text style={styles.carouselCheckInBtnText}>Check In</Text>
                      </TouchableOpacity>
                    )}
                  </TouchableOpacity>
                </View>
              );
            })() : (
              <View style={styles.noUpcomingCard}>
                <Text style={styles.noUpcomingText}>No upcoming filled positions</Text>
                <TouchableOpacity
                  style={styles.noUpcomingBtn}
                  onPress={() => navigation.navigate('PostJob')}
                >
                  <Text style={styles.noUpcomingBtnText}>Post a Job</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        ) : (
          // Full-width CTA for non-Locum categories
          <TouchableOpacity
            style={[styles.postJobCtaExpanded, isMobile && styles.postJobCtaExpandedMobile]}
            onPress={() => navigation.navigate('PostJob')}
          >
            <View style={[styles.ctaContentExpanded, isMobile && styles.ctaContentMobile]}>
              <View style={styles.ctaIconExpanded}>
                <Briefcase size={24} color="#ffffff" />
              </View>
              <View style={[styles.ctaTextExpanded, isMobile && styles.ctaTextMobile]}>
                <Text style={styles.ctaTitleExpanded}>Post a Job</Text>
                <Text style={styles.ctaSubtitleExpanded}>
                  Find quality healthcare professionals for your {categories[activeCategory]?.label} positions.
                </Text>
              </View>
            </View>
            <TouchableOpacity style={styles.ctaBtnExpanded} onPress={() => navigation.navigate('PostJob')}>
              <Text style={styles.ctaBtnTextExpanded}>Post Job →</Text>
            </TouchableOpacity>
          </TouchableOpacity>
        )}
            {/* Application Status Chart */}
            <View style={styles.chartCard}>
          <View style={styles.chartHeader}>
            <Text style={styles.chartTitle}>Job Status Overview</Text>
          </View>
          <View style={styles.chartContainer}>
            {chartData.map((bar, index) => (
              <View key={index} style={styles.chartBarWrapper}>
                <View style={[styles.chartBar, { height: Math.max(bar.height, 10), backgroundColor: bar.color }]} />
                <Text style={styles.chartBarLabel}>{bar.label}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Job Listings Section */}
        <View style={styles.jobListingsCard}>
          <View style={styles.jobListingsHeader}>
            <Text style={styles.jobListingsTitle}>Job Listings</Text>
            <TouchableOpacity
              style={styles.sortDropdownWrapper}
              onPress={() => setShowSortDropdown(!showSortDropdown)}
            >
              <View style={styles.sortPill}>
                <Text style={styles.sortPillText}>Sort by: </Text>
                <Text style={styles.sortPillValue}>{sortBy}</Text>
                <View style={[styles.sortArrow, showSortDropdown && styles.sortArrowOpen]}>
                  <ChevronDown size={12} color={webColors.textMuted} />
                </View>
              </View>
              {showSortDropdown && (
                <View style={styles.sortDropdownMenu}>
                  {['Latest', 'Oldest', 'Urgent'].map(option => (
                    <TouchableOpacity
                      key={option}
                      style={[styles.sortDropdownItem, sortBy === option && styles.sortDropdownItemActive]}
                      onPress={() => { setSortBy(option); setShowSortDropdown(false); }}
                    >
                      <Text style={[styles.sortDropdownItemText, sortBy === option && styles.sortDropdownItemTextActive]}>
                        {option}
                      </Text>
                      {sortBy === option && <Check size={14} color={webColors.primary} />}
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </TouchableOpacity>
          </View>

          {/* Profession Filter Tabs */}
          {professionTabs.length > 1 && (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.professionTabsScroll}>
              <View style={styles.professionTabs}>
                {professionTabs.map((tab, index) => (
                  <TouchableOpacity
                    key={tab.id}
                    style={[styles.professionTab, selectedProfessionFilter === index && styles.professionTabActive]}
                    onPress={() => setSelectedProfessionFilter(index)}
                  >
                    <Text style={[styles.professionTabText, selectedProfessionFilter === index && styles.professionTabTextActive]}>
                      {tab.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          )}

          {/* Status Tabs */}
          <View style={styles.statusTabsRow}>
            {statusTabs.map(tab => (
              <TouchableOpacity
                key={tab.id}
                style={[styles.statusTab, activeStatusTab === tab.id && styles.statusTabActive]}
                onPress={() => tab.id === 'history' ? navigation.navigate('JobHistory') : setActiveStatusTab(tab.id)}
              >
                <Text style={[styles.statusTabText, activeStatusTab === tab.id && styles.statusTabTextActive]}>
                  {tab.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Job Cards List */}
          {isLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={webColors.primary} />
            </View>
          ) : sortedJobs.length > 0 ? (
            <View style={styles.jobsList}>
              {sortedJobs.map((job, index) => (
                <JobCard
                  key={job.uuid || index}
                  job={job}
                  onPress={() => {
                    const isDraft = parseInt(job.type) <= -10;
                    if (isDraft) {
                      navigation.navigate('PostJob', { editJobData: job });
                    } else {
                      setSelectedJob(job); setShowJobModal(true);
                    }
                  }}
                  onApprove={(applicant) => handleApproveApplicant(job, applicant)}
                  onReject={(applicant) => handleRejectApplicant(job, applicant)}
                  approvingUuid={approveMutation.isPending ? approveMutation.variables?.uuid : null}
                  rejectingUuid={rejectMutation.isPending ? rejectMutation.variables?.uuid : null}
                />
              ))}
            </View>
          ) : (
            <View style={styles.jobsEmptyState}>
              <View style={styles.emptyStateIcon}>
                <ClipboardList size={48} color="#9ca3af" />
              </View>
              <Text style={styles.emptyStateText}>No {activeStatusTab} jobs</Text>
              <TouchableOpacity onPress={() => navigation.navigate('PostJob')}>
                <Text style={styles.emptyStateLink}>Post a new job</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>


        {/* Quick Actions + Recent Activity Grid */}
        <View style={[styles.actionsActivityGrid, isMobile && styles.actionsActivityGridMobile]}>
          {/* Quick Actions */}
          <View style={[styles.quickActionsCard, isMobile && styles.activityPanelMobile]}>
            <Text style={styles.quickActionsHeader}>Quick Actions</Text>
            {quickActions.map((action, index) => (
              <TouchableOpacity key={index} style={styles.quickActionRow} onPress={action.action}>
                <View style={[styles.quickActionIconBox, { backgroundColor: action.bg }]}>
                  <action.Icon size={14} color={webColors.textPrimary} />
                </View>
                <Text style={styles.quickActionLabel}>{action.label}</Text>
                <ChevronRight size={16} color="#d1d5db" />
              </TouchableOpacity>
            ))}
          </View>

          {/* Recent Activity */}
          <View style={[styles.recentActivityCard, isMobile && styles.activityPanelMobile]}>
            <View style={styles.recentActivityHeader}>
              <Text style={styles.recentActivityTitle}>Recent Activity</Text>
              <TouchableOpacity onPress={() => navigation.navigate('Inbox')}>
                <Text style={styles.recentActivityLink}>View All →</Text>
              </TouchableOpacity>
            </View>
            {recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => (
                <View key={index} style={styles.activityRow}>
                  <View style={[styles.activityIndicator, activity.type === 'approved' ? styles.activityApproved : activity.type === 'applied' ? styles.activityApplied : styles.activityPosted]}>
                    {activity.type === 'approved' && <Check size={10} color="#22c55e" />}
                  </View>
                  <View style={styles.activityInfo}>
                    <Text style={styles.activityText}>
                      <Text style={styles.activityTextBold}>{activity.title}</Text>
                    </Text>
                    <Text style={styles.activityTextSub}>{activity.text}</Text>
                    <Text style={styles.activityTime}>{activity.time}</Text>
                  </View>
                </View>
              ))
            ) : (
              <View style={styles.emptyActivity}>
                <Text style={styles.emptyActivityText}>No recent activity</Text>
              </View>
            )}
          </View>
        </View>

    
        <View style={{ height: 60 }} />
      </ScrollView>

      {/* Job Application Modal */}
      <JobDescriptionModal
        visible={showJobModal}
        job={selectedJob}
        onClose={() => { setShowJobModal(false); setSelectedJob(null); }}
        onEdit={(job) => {
          setShowJobModal(false);
          setSelectedJob(null);
          navigation.navigate('PostJob', { editJobData: job });
        }}
        onRefresh={handleRefresh}
      />

      {/* Upcoming Jobs Modal */}
      <UpcomingJobsModal
        visible={showUpcomingModal}
        jobs={upcomingJobs}
        onClose={() => setShowUpcomingModal(false)}
        onCheckIn={handleDirectCheckIn}
      />

      {/* Check-in Confirmation Modal.
          Mounted conditionally so its portal is appended to <body> at open time and
          stacks above any already-open modal (e.g. the Upcoming Jobs modal). */}
      {showCheckInConfirm && (
        <Modal
          visible
          transparent
          animationType="fade"
          onRequestClose={closeCheckInModal}
        >
          <Pressable style={styles.confirmOverlay} onPress={closeCheckInModal}>
            <Pressable style={styles.confirmModal} onPress={e => e.stopPropagation()}>
              <Text style={styles.confirmTitle}>Check In Doctor</Text>

              {showCheckInQR ? (
                <View style={styles.checkInQrView}>
                  {checkInQrLoading ? (
                    <ActivityIndicator size="large" color={webColors.primary} style={{ marginVertical: 40 }} />
                  ) : checkInQrToken ? (
                    <>
                      <QRCode value={checkInQrToken} size={220} logoBackgroundColor="transparent" />
                      <Text style={styles.checkInQrInstructions}>
                        Have the doctor scan this code to check in for{' '}
                        <Text style={{ fontWeight: '600' }}>{checkInJob?.title}</Text>.
                      </Text>
                    </>
                  ) : (
                    <Text style={styles.checkInQrError}>Unable to load QR code</Text>
                  )}
                  <TouchableOpacity
                    style={styles.checkInQrBackBtn}
                    onPress={() => setShowCheckInQR(false)}
                  >
                    <Text style={styles.checkInQrBackBtnText}>‹ Back</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <>
                  <Text style={styles.confirmBody}>
                    Are you sure you want to check in the doctor for{' '}
                    <Text style={{ fontWeight: '600' }}>{checkInJob?.title}</Text>?
                  </Text>
                  {checkInJob?.start_timestamp && (
                    <Text style={styles.confirmJobTime}>
                      {moment(checkInJob.start_timestamp).format('D MMM, h:mm A')} - {moment(checkInJob.end_timestamp).format('h:mm A')}
                    </Text>
                  )}
                  <TouchableOpacity
                    style={styles.showQrFullBtn}
                    onPress={() => setShowCheckInQR(true)}
                    disabled={directCheckIn.isLoading}
                  >
                    <QrCode size={16} color={webColors.primary} />
                    <Text style={styles.showQrFullBtnText}>Show QR Code</Text>
                  </TouchableOpacity>
                  <View style={styles.confirmBtnRow}>
                    <TouchableOpacity
                      style={styles.confirmBtnCancel}
                      onPress={closeCheckInModal}
                      disabled={directCheckIn.isLoading}
                    >
                      <Text style={styles.confirmBtnCancelText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.confirmBtn, directCheckIn.isLoading && { opacity: 0.6 }]}
                      onPress={confirmCheckIn}
                      disabled={directCheckIn.isLoading}
                    >
                      {directCheckIn.isLoading ? (
                        <ActivityIndicator size="small" color="#ffffff" />
                      ) : (
                        <Text style={styles.confirmBtnText}>Confirm Check In</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                </>
              )}
            </Pressable>
          </Pressable>
        </Modal>
      )}
    </WebLayout>
  );
};

// Job Card Component
const JobCard = ({ job, onPress, onApprove, onReject, approvingUuid, rejectingUuid }) => {
  const isMobile = useIsMobile();
  const [isHovered, setIsHovered] = React.useState(false);
  const applicantCount = job.job_applications?.length || 0;

  // Non-locum jobs (full-time, temporary, contract, international) don't have a
  // meaningful shift start/end time, so the date pill shows the posted and expiry
  // dates instead. Draft types are stored as -(apiType + 10), so unwrap before checking.
  const rawType = parseInt(job.type);
  const baseType = rawType <= -10 ? -rawType - 10 : rawType;
  const isNonLocum = isFullTimeJob(baseType);

  // Applicants awaiting the facility's approval (status 0 / not yet acted on)
  const pendingApplicants = (job.job_applications || []).filter(a => {
    const code = a.status_code ?? a.status ?? a.job_application_status_code;
    return code === 0 || code == null;
  });
  // Inline Approve/Reject only when exactly one applicant is pending and the job
  // is not full-time (full-time uses a different approval flow in the modal).
  const singlePending =
    !isFullTimeJob(job.type) && pendingApplicants.length === 1 ? pendingApplicants[0] : null;
  const canInlineAct = singlePending && onApprove && onReject;
  const isApproving = canInlineAct && approvingUuid === singlePending.uuid;
  const isRejecting = canInlineAct && rejectingUuid === singlePending.uuid;

  return (
    <TouchableOpacity
      style={[styles.jobCard, isHovered && styles.jobCardHover, isMobile && styles.jobCardMobile]}
      onPress={onPress}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Date Pill */}
      <View style={[styles.jobDatePill, isMobile && styles.jobDatePillMobile]}>
        {isNonLocum ? (
          <>
            <Text style={[styles.jobDatePillLabel, isMobile && styles.jobDateLabelMobile]}>Posted</Text>
            <Text style={[styles.jobDatePillDate, isMobile && styles.jobDateValueMobile]}>
              {moment(job.created_at || job.start_timestamp).format('D MMM')}
            </Text>
            <View style={styles.jobDatePillDivider} />
            <Text style={[styles.jobDatePillLabel, isMobile && styles.jobDateLabelMobile]}>Expires</Text>
            <Text style={[styles.jobDatePillDate, isMobile && styles.jobDateValueMobile]}>
              {moment(job.end_timestamp).format('D MMM')}
            </Text>
          </>
        ) : (
          <>
            <Text style={[styles.jobDateMonth, isMobile && styles.jobDateLabelMobile]}>
              {moment(job.start_timestamp).format('MMM').toUpperCase()}
            </Text>
            <Text style={[styles.jobDateDay, isMobile && styles.jobDateDayMobile]}>
              {moment(job.start_timestamp).format('D')}
            </Text>
            <Text style={[styles.jobDateTime, isMobile && styles.jobDateTimeMobile]}>
              {moment(job.start_timestamp).format('h:mm A')}
            </Text>
            <Text style={[styles.jobDateTime, isMobile && styles.jobDateTimeMobile]}>
              {moment(job.end_timestamp).format('h:mm A')}
            </Text>
          </>
        )}
      </View>

      <View style={[styles.jobMain, isMobile && styles.jobMainMobile]}>
        {/* Job Info */}
        <View style={[styles.jobInfo, isMobile && styles.jobInfoMobile]}>
        {job.subtitle ? (
            <View style={[styles.jobSubtitlePill, isMobile && styles.jobSubtitlePillMobile]}>
              <Text style={[styles.jobSubtitleText, isMobile && styles.jobTagTextMobile]}>{job.subtitle}</Text>
            </View>
          ) : null}
          <Text style={[styles.jobTypeLabel, isMobile && styles.jobTypeLabelMobile]}>{job.clinic?.type || job.facility_type || 'Clinic'}</Text>
          <Text style={[styles.jobTitle, isMobile && styles.jobTitleMobile]} numberOfLines={isMobile ? undefined : 1}>{job.title}</Text>

          <Text style={[styles.jobClinic, isMobile && styles.jobClinicMobile]}>{job.clinic?.name || job.clinic_name || 'Healthcare Facility'}</Text>
          <View style={[styles.jobTags, isMobile && styles.jobTagsMobile]}>
            {(() => {
              const isInternational = job.type === 4 || job.type === '4' || !!job.international;
              const currency = isInternational ? 'USD' : (job.country || 'MY');

              const formatSalary = () => {
                if (job.price && job.price_max && Number(job.price_max) > Number(job.price)) {
                  return formatPriceRange(job.price, job.price_max, currency);
                }
                if (job.price) return formatPrice(job.price, currency);
                return null;
              };
              const salaryText = formatSalary();

              return (
                <>
                  {isInternational && job.country && (
                    <View style={[styles.countryTag, isMobile && styles.jobTagMobile]}>
                      <Text style={[styles.countryTagText, isMobile && styles.jobTagTextMobile]}>
                        {`Open to: ${getWorldCountryData(job.country)?.flag || ''} ${getWorldCountryData(job.country)?.name || 'International'}`.trim()}
                      </Text>
                    </View>
                  )}
                  {isInternational && job.international && (
                    <View style={[styles.countryTag, isMobile && styles.jobTagMobile]}>
                      <Text style={[styles.countryTagText, isMobile && styles.jobTagTextMobile]}>
                        {`Location: ${getWorldCountryData(job.international)?.flag || ''} ${getWorldCountryData(job.international)?.name || 'International'}`.trim()}
                      </Text>
                    </View>
                  )}
                  {salaryText && (
                    <View style={[styles.salaryTag, isMobile && styles.jobTagMobile]}>
                      <Text style={[styles.salaryTagText, isMobile && styles.jobTagTextMobile]}>{salaryText}</Text>
                    </View>
                  )}
                </>
              );
            })()}
            {job.is_cash && (
              <View style={[styles.paymentTag, isMobile && styles.jobTagMobile]}>
                <Text style={[styles.paymentTagText, isMobile && styles.jobTagTextMobile]}>CASH</Text>
              </View>
            )}
            {job.duration_hours && (
              <View style={[styles.durationTag, isMobile && styles.jobTagMobile]}>
                <Text style={[styles.durationTagText, isMobile && styles.jobTagTextMobile]}>{job.duration_hours}hr</Text>
              </View>
            )}
          </View>
        </View>
        <View style={isMobile && styles.jobFooterMobile}>
        {/* Candidate Count */}
          <View style={[styles.jobCandidates, isMobile && styles.jobCandidatesMobile]}>
            <Text style={[styles.jobCandidatesCount, isMobile && styles.jobCandidatesCountMobile]}>{applicantCount}</Text>
            <Text style={[styles.jobCandidatesLabel, isMobile && styles.jobCandidatesLabelMobile]}>
              {isMobile ? (applicantCount === 1 ? 'Applicant' : 'Applicants') : 'Candidate\nApplied'}
            </Text>
          </View>

          {/* Approve/Reject for a single pending applicant, otherwise View */}
          {canInlineAct ? (
            <View style={[styles.jobActions, isMobile && styles.jobActionsMobile]}>
              <TouchableOpacity
                style={[styles.btnApprove, isMobile && styles.jobActionButtonMobile, (isApproving || isRejecting) && styles.btnDisabled]}
                onPress={(e) => { e.stopPropagation(); onApprove(singlePending); }}
                disabled={isApproving || isRejecting}
              >
                {isApproving ? <Hourglass size={14} color="#ffffff" /> : <Check size={14} color="#ffffff" />}
                <Text style={styles.btnApproveText}>{isApproving ? 'Approving...' : 'Approve'}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.btnReject, isMobile && styles.jobActionButtonMobile, (isApproving || isRejecting) && styles.btnDisabled]}
                onPress={(e) => { e.stopPropagation(); onReject(singlePending); }}
                disabled={isApproving || isRejecting}
              >
                {isRejecting ? <Hourglass size={14} color="#ffffff" /> : <X size={14} color="#ffffff" />}
                <Text style={styles.btnRejectText}>{isRejecting ? 'Rejecting...' : 'Reject'}</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity style={[styles.btnViewJob, isMobile && styles.jobActionButtonMobile]} onPress={onPress}>
              <Text style={styles.btnViewJobText}>View</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </TouchableOpacity>

  );
};

// Helper functions

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
};

const formatTimeAgo = (date) => {
  if (!date) return '';
  const m = moment(date);
  const now = moment();
  const diff = now.diff(m, 'hours');
  if (diff < 1) return 'Just now';
  if (diff < 24) return `${diff} hours ago`;
  const days = now.diff(m, 'days');
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return m.format('MMM D');
};

const getStatusLabel = (statusCode, job) => {
  const hasApplications = job?.job_applications?.length > 0;
  const isExpired = moment(job?.end_timestamp).isBefore(moment());

  if (!hasApplications && !isExpired) return 'Unfilled';

  const labels = {
    0: 'Pending',
    1: 'Pending',
    2: 'Pending',
    3: 'Approved',
    4: 'Completed',
    '-2': 'Cancelled',
    '-3': 'Expired',
  };

  if (isExpired && statusCode !== 4) return 'Expired';
  return labels[statusCode] || 'Unfilled';
};

const getStatusBadgeStyle = (statusCode, isDraft) => {
  if (isDraft) {
    return { backgroundColor: '#f3e8ff', textColor: '#7c3aed' };
  }

  const styles = {
    0: { backgroundColor: '#fef3c7', textColor: '#92400e' },
    1: { backgroundColor: '#fef3c7', textColor: '#92400e' },
    2: { backgroundColor: '#fef3c7', textColor: '#92400e' },
    3: { backgroundColor: '#dbeafe', textColor: '#1d4ed8' },
    4: { backgroundColor: '#d1f4e0', textColor: '#0d9f6e' },
    '-2': { backgroundColor: '#fee2e2', textColor: '#991b1b' },
    '-3': { backgroundColor: '#fef3c7', textColor: '#92400e' },
  };
  return styles[statusCode] || { backgroundColor: '#fef3c7', textColor: '#92400e' };
};

const getJobTypeName = (type) => {
  const typeNum = parseInt(type);
  if (typeNum <= -10) return 'Draft';
  const jobType = JOB_TYPES.find(t => t.apiId === typeNum);
  return jobType?.name || 'Locum';
};

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  headerActions: {
    flexDirection: 'row',
    gap: 12,
  },
  btnPostJob: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
    ...webShadows.button,
  },
  btnPostJobIcon: {
    fontSize: 18,
    color: '#ffffff',
    fontWeight: '600',
  },
  btnPostJobText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  // Category Tabs
  categoryTabs: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 24,
  },
  categoryTab: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 22,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: webColors.border,
    backgroundColor: webColors.cardBackground,
    alignItems: 'center',
    position: 'relative',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    overflow: 'visible',
  },
  categoryTabActive: {
    backgroundColor: webColors.primary,
    borderColor: webColors.primary,
  },
  categoryTabText: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textMuted,
  },
  categoryTabTextActive: {
    color: '#ffffff',
  },
  categoryBadge: {
    position: 'absolute',
    top: -6,
    right: -6,
    minWidth: 20,
    height: 20,
    backgroundColor: webColors.primary,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 5,
    borderWidth: 2,
    borderColor: '#f8f9fa',
  },
  categoryBadgeActive: {
    backgroundColor: '#be185d',
  },
  categoryBadgeText: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: '600',
  },
  // Stats Row
  statsRow: {
    flexDirection: 'row',
    backgroundColor: webColors.cardBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: webColors.border,
    overflow: 'hidden',
    marginBottom: 24,
  },
  statCard: {
    flex: 1,
    padding: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  statCardBorder: {
    borderRightWidth: 1,
    borderRightColor: '#f3f4f6',
  },
  statIconBox: {
    width: 40,
    height: 40,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statIcon: {
    fontSize: 17,
  },
  statValue: {
    fontSize: 26,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  statLabel: {
    fontSize: 12,
    color: webColors.textMuted,
    lineHeight: 16,
  },
  // Top Row Grid
  topRowGrid: {
    flexDirection: 'row',
    gap: 24,
    marginBottom: 24,
  },
  // Post CTA
  postJobCta: {
    flex: 1,
    background: 'linear-gradient(135deg, #667eea 0%, #EC4899 100%)',
    backgroundColor: '#EC4899',
    borderRadius: 16,
    padding: 24,
    paddingHorizontal: 28,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
  },
  ctaContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  ctaIcon: {
    width: 44,
    height: 44,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaIconText: {},
  ctaText: {},
  ctaTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: 2,
  },
  ctaSubtitle: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.85)',
  },
  ctaBtn: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
  },
  ctaBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  // Expanded CTA
  postJobCtaExpanded: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #EC4899 100%)',
    backgroundColor: '#667eea',
    borderRadius: 16,
    padding: 24,
    paddingHorizontal: 32,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    marginBottom: 24,
  },
  ctaContentExpanded: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
    flex: 1,
  },
  ctaIconExpanded: {
    width: 56,
    height: 56,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaIconTextExpanded: {},
  ctaTextExpanded: {},
  ctaTitleExpanded: {
    fontSize: 18,
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: 4,
  },
  ctaSubtitleExpanded: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.9)',
    lineHeight: 20,
    maxWidth: 500,
  },
  ctaBtnExpanded: {
    paddingHorizontal: 28,
    paddingVertical: 14,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
  },
  ctaBtnTextExpanded: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  // Upcoming Card
  upcomingCard: {
    flex: 1,
    background: 'linear-gradient(135deg, #fdf2f8 0%, #faf5ff 100%)',
    backgroundColor: '#fdf2f8',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#fce7f3',
    padding: 16,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  datePill: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
    minWidth: 70,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
  },
  dateMonth: {
    fontSize: 10,
    fontWeight: '600',
    color: '#EC4899',
    textTransform: 'uppercase',
  },
  dateDay: {
    fontSize: 26,
    fontWeight: '700',
    color: 'black',
    lineHeight: 30,
  },
  upcomingInfo: {
    flex: 1,
  },
  upcomingTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  upcomingLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#EC4899',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  upcomingCountBadge: {
    backgroundColor: '#EC4899',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  upcomingCountBadgeText: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: '700',
  },
  upcomingTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textPrimary,
    lineHeight: 20,
  },
  upcomingCompany: {
    fontSize: 12,
    color: webColors.textMuted,
    marginTop: 2,
  },
  btnViewDetails: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
  },
  btnViewDetailsText: {
    fontSize: 12,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  // Upcoming Jobs Carousel
  upcomingCarousel: {
    flex: 1,
    backgroundColor: webColors.cardBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#fce7f3',
    overflow: 'hidden',
  },
  carouselHeader: {
    background: 'linear-gradient(135deg, #EC4899 0%, #f472b6 100%)',
    backgroundColor: '#EC4899',
    paddingVertical: 10,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  carouselHeaderText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#ffffff',
    letterSpacing: 0.5,
  },
  carouselCountBadge: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  carouselCountBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#EC4899',
  },
  carouselBody: {
    padding: 16,
  },
  carouselContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  carouselNavBtn: {
    padding: 6,
    borderRadius: 50,
    backgroundColor: 'transparent',
  },
  carouselNavBtnDisabled: {
    opacity: 0.4,
  },
  carouselJobContent: {
    flex: 1,
    minWidth: 0,
  },
  carouselJobTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 2,
  },
  carouselJobClinic: {
    fontSize: 13,
    color: webColors.textSecondary,
    marginBottom: 10,
  },
  carouselJobMeta: {
    flexDirection: 'row',
    gap: 16,
  },
  carouselMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  carouselMetaText: {
    fontSize: 13,
    color: webColors.textSecondary,
    fontWeight: '500',
  },
  carouselIncentiveBadge: {
    alignSelf: 'flex-start',
    backgroundColor: '#dcfce7',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginTop: 10,
  },
  carouselIncentiveBadgeText: {
    fontSize: 11,
    color: '#22c55e',
    fontWeight: '600',
  },
  carouselDots: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
    marginBottom: 16,
  },
  carouselDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#e5e7eb',
  },
  carouselDotActive: {
    width: 20,
    backgroundColor: '#EC4899',
  },
  carouselCheckInBtn: {
    backgroundColor: '#EC4899',
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  carouselCheckInBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  carouselCheckedInBadge: {
    backgroundColor: '#dcfce7',
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
  },
  carouselCheckedInText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#22c55e',
  },
  // Confirm Modal
  confirmOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  confirmModal: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    ...webShadows.modal,
  },
  confirmTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: 12,
  },
  confirmBody: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
    marginBottom: 8,
  },
  confirmJobTime: {
    fontSize: 13,
    color: '#9ca3af',
    marginBottom: 20,
  },
  confirmBtnRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 4,
  },
  confirmBtnCancel: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
  },
  confirmBtnCancelText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6b7280',
  },
  confirmBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
    backgroundColor: '#22c55e',
  },
  confirmBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  // Show QR (inside Check In Doctor modal)
  showQrFullBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    backgroundColor: '#ffffff',
    marginBottom: 12,
  },
  showQrFullBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.primary,
  },
  checkInQrView: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  checkInQrInstructions: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 20,
    marginTop: 20,
  },
  checkInQrError: {
    fontSize: 14,
    color: '#9ca3af',
    marginVertical: 40,
  },
  checkInQrBackBtn: {
    alignSelf: 'stretch',
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: '#f3f4f6',
    marginTop: 20,
  },
  checkInQrBackBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6b7280',
  },
  // No upcoming
  noUpcomingCard: {
    flex: 1,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: webColors.border,
    padding: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  noUpcomingIcon: {
    fontSize: 32,
    marginBottom: 8,
  },
  noUpcomingText: {
    color: webColors.textMuted,
    fontSize: 14,
    marginBottom: 12,
  },
  noUpcomingBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.small,
  },
  noUpcomingBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
  // Actions Activity Grid
  actionsActivityGrid: {
    flexDirection: 'row',
    gap: 20,
    marginTop: 24,
  },
  // Quick Actions Card
  quickActionsCard: {
    flex: 1,
    backgroundColor: webColors.cardBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: webColors.border,
    overflow: 'hidden',
  },
  quickActionsHeader: {
    padding: 14,
    paddingHorizontal: 16,
    fontSize: 13,
    fontWeight: '700',
    color: webColors.textPrimary,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  quickActionRow: {
    padding: 12,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  quickActionIconBox: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickActionIcon: {},
  quickActionLabel: {
    flex: 1,
    fontSize: 13,
    fontWeight: '500',
    color: '#374151',
  },
  // Recent Activity Card
  recentActivityCard: {
    flex: 1,
    backgroundColor: webColors.cardBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: webColors.border,
    overflow: 'hidden',
  },
  recentActivityHeader: {
    padding: 14,
    paddingHorizontal: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  recentActivityTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  recentActivityLink: {
    fontSize: 12,
    fontWeight: '500',
    color: webColors.primary,
  },
  activityRow: {
    padding: 12,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  activityIndicator: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  activityApplied: {
    backgroundColor: '#fce7f3',
  },
  activityApproved: {
    backgroundColor: '#dcfce7',
  },
  activityPosted: {
    backgroundColor: '#dbeafe',
  },
  activityCheck: {},
  activityInfo: {
    flex: 1,
  },
  activityText: {
    fontSize: 13,
    color: '#374151',
    lineHeight: 18,
  },
  activityTextBold: {
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  activityTextSub: {
    fontSize: 12,
    color: webColors.textMuted,
    marginTop: 2,
  },
  activityTime: {
    fontSize: 11,
    color: '#9ca3af',
    marginTop: 4,
  },
  emptyActivity: {
    padding: 24,
    alignItems: 'center',
  },
  emptyActivityText: {
    fontSize: 13,
    color: webColors.textMuted,
  },
  // Chart Card
  chartCard: {
    backgroundColor: webColors.cardBackground,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: webColors.border,
    padding: 24,
    marginBottom: 24,
  },
  chartHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  chartContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-around',
    height: 140,
    paddingHorizontal: 10,
  },
  chartBarWrapper: {
    alignItems: 'center',
    gap: 8,
  },
  chartBar: {
    width: 70,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  chartBarValue: {
    fontSize: 14,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  chartBarLabel: {
    fontSize: 12,
    color: webColors.textMuted,
  },
  // Job Listings Card
  jobListingsCard: {
    backgroundColor: webColors.cardBackground,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: webColors.border,
    padding: 24,
  },
  jobListingsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  jobListingsTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  sortDropdownWrapper: {
    position: 'relative',
    zIndex: 100,
  },
  sortPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: 20,
  },
  sortPillText: {
    fontSize: 13,
    color: webColors.textMuted,
  },
  sortPillValue: {
    fontSize: 13,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  sortArrow: {},
  sortArrowOpen: {
    transform: [{ rotate: '180deg' }],
  },
  sortDropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 8,
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 16,
    minWidth: 150,
    overflow: 'hidden',
    zIndex: 100,
  },
  sortDropdownItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  sortDropdownItemActive: {
    backgroundColor: '#f9fafb',
  },
  sortDropdownItemText: {
    fontSize: 14,
    color: '#374151',
  },
  sortDropdownItemTextActive: {
    color: webColors.primary,
    fontWeight: '600',
  },
  // Profession Tabs
  professionTabsScroll: {
    marginBottom: 16,
  },
  professionTabs: {
    flexDirection: 'row',
    gap: 10,
  },
  professionTab: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#f3f4f6',
  },
  professionTabActive: {
    backgroundColor: '#dbeafe',
  },
  professionTabText: {
    fontSize: 13,
    fontWeight: '500',
    color: webColors.textMuted,
  },
  professionTabTextActive: {
    color: '#1d4ed8',
    fontWeight: '600',
  },
  // Status Tabs
  statusTabsRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  statusTab: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: webColors.border,
    backgroundColor: webColors.cardBackground,
  },
  statusTabActive: {
    backgroundColor: '#1a1a1a',
    borderColor: '#1a1a1a',
  },
  statusTabText: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textMuted,
  },
  statusTabTextActive: {
    color: '#ffffff',
  },
  historyBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  historyBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.primary,
  },
  // Job Cards
  jobsList: {
    gap: 16,
  },
  jobCard: {
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 16,
    padding: 20,
    paddingHorizontal: 24,
    flexDirection: 'row',
    gap: 20,
    alignItems: 'center',
    cursor: 'pointer',
  },
  jobCardHover: {
    borderColor: '#EC4899',
    shadowColor: 'rgba(236, 72, 153, 0.1)',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 12,
  },
  jobDatePill: {
    background: 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)',
    backgroundColor: '#06b6d4',
    borderRadius: 14,
    padding: 14,
    paddingHorizontal: 18,
    alignItems: 'center',
    minWidth: 90,
    flexShrink: 0,
  },
  jobDateMonth: {
    fontSize: 11,
    fontWeight: '700',
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  jobDateDay: {
    fontSize: 32,
    fontWeight: '700',
    color: '#ffffff',
    lineHeight: 36,
  },
  jobDateTime: {
    fontSize: 10,
    color: 'rgba(255,255,255,0.9)',
    marginTop: 4,
    lineHeight: 14,
  },
  // Posted / Expiry date pill (non-locum jobs)
  jobDatePillLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: 'rgba(255,255,255,0.85)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  jobDatePillDate: {
    fontSize: 15,
    fontWeight: '700',
    color: '#ffffff',
    marginTop: 2,
    lineHeight: 18,
  },
  jobDatePillDivider: {
    width: '70%',
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.3)',
    marginVertical: 8,
  },
  jobMain: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
  },
  jobInfo: {
    flex: 1,
  },
  jobTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 4,
  },
  jobSubtitlePill: {
    alignSelf: 'flex-start',
    background: 'linear-gradient(135deg, #818cf8 0%, #7c3aed 100%)',
    backgroundColor: '#7c3aed',
    borderRadius: 20,
    paddingHorizontal: 14,
    paddingVertical: 6,
    marginBottom: 8,
  },
  jobSubtitleText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
  jobClinic: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 12,
    fontWeight: '400',
  },
  jobTags: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  // New tag styles matching reference design
  jobTypeLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 4,
  },
  countryTag: {
    backgroundColor: '#e0e7ff',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  countryTagText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#3730a3',
  },
  salaryTag: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#fce7f3',
  },
  salaryTagText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#EC4899',
  },
  paymentTag: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  paymentTagText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
  },
  durationTag: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  durationTagText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
  },
  jobCandidates: {
    alignItems: 'center',
    minWidth: 80,
    flexShrink: 0,
  },
  jobCandidatesCount: {
    fontSize: 36,
    fontWeight: '700',
    color: '#1a1a1a',
    lineHeight: 40,
  },
  jobCandidatesLabel: {
    fontSize: 12,
    color: webColors.textMuted,
    textAlign: 'center',
    lineHeight: 16,
  },
  btnViewJob: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    backgroundColor: '#EC4899',
    borderRadius: 10,
    marginTop: 10,
  },
  btnViewJobText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
  // Inline Approve/Reject actions
  jobActions: {
    flexDirection: 'column',
    gap: 8,
    marginTop: 10,
  },
  btnApprove: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#22c55e',
    borderRadius: 10,
  },
  btnApproveText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
  btnReject: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#ef4444',
    borderRadius: 10,
  },
  btnRejectText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
  btnDisabled: {
    opacity: 0.5,
  },
  // Empty State
  jobsEmptyState: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyStateIcon: {
    marginBottom: 16,
    opacity: 0.5,
  },
  emptyStateText: {
    fontSize: 15,
    color: '#9ca3af',
    marginBottom: 12,
  },
  emptyStateLink: {
    fontSize: 15,
    color: webColors.primary,
    fontWeight: '600',
  },
  // ==================== MOBILE VARIANTS ====================
  pageTitleMobile: {
    fontSize: 22,
  },
  categoryTabsMobile: {
    flexWrap: 'wrap',
  },
  categoryTabMobile: {
    flexBasis: '47%',
    flexGrow: 1,
  },
  topRowGridMobile: {
    flexDirection: 'column',
  },
  postJobCtaMobile: {
    flex: 'none',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    paddingBottom: 24,
    gap: 16,
  },
  ctaContentMobile: {
    flex: 'none',
    width: '100%',
  },
  ctaTextMobile: {
    flex: 1,
    minWidth: 0,
  },
  postJobCtaExpandedMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16,
    paddingHorizontal: 20,
  },
  actionsActivityGridMobile: {
    flexDirection: 'column',
  },
  activityPanelMobile: {
    flex: 'none',
    width: '100%',
  },
  jobCardMobile: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 12,
    gap: 12,
  },
  jobDatePillMobile: {
    width: 64,
    minWidth: 64,
    paddingHorizontal: 6,
    paddingVertical: 12,
    borderRadius: 10,
  },
  jobDateLabelMobile: {
    fontSize: 9,
    lineHeight: 12,
  },
  jobDateDayMobile: {
    fontSize: 26,
    lineHeight: 30,
  },
  jobDateTimeMobile: {
    fontSize: 9,
    lineHeight: 12,
  },
  jobDateValueMobile: {
    fontSize: 12,
    lineHeight: 16,
  },
  jobMainMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
  },
  jobInfoMobile: {
    flex: 'none',
    minWidth: 0,
  },
  jobSubtitlePillMobile: {
    maxWidth: '100%',
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginBottom: 6,
  },
  jobTypeLabelMobile: {
    fontSize: 10,
    marginBottom: 2,
  },
  jobTitleMobile: {
    fontSize: 13,
    lineHeight: 18,
  },
  jobClinicMobile: {
    fontSize: 11,
    lineHeight: 16,
    marginBottom: 8,
  },
  jobTagsMobile: {
    gap: 6,
  },
  jobTagMobile: {
    maxWidth: '100%',
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  jobTagTextMobile: {
    fontSize: 10,
    lineHeight: 14,
  },
  jobFooterMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
  },
  jobCandidatesMobile: {
    minWidth: 0,
    flexDirection: 'row',
    gap: 4,
  },
  jobCandidatesCountMobile: {
    fontSize: 18,
    lineHeight: 24,
  },
  jobCandidatesLabelMobile: {
    fontSize: 10,
    lineHeight: 14,
  },
  jobActionsMobile: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 0,
    gap: 6,
  },
  jobActionButtonMobile: {
    minHeight: 44,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginTop: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default HomeFacilityScreen;
